import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Header } from '@components/Header';
import HistoryIcon from '@components/icons/History.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TagDefaultRounded } from '@components/core-ui/Tag/variations/TagDefaultRounded';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { formatPrice } from '@helpers/formatPrice';
import { isExternallyManagedOrganization } from '@helpers/isExternallyManagedOrganization';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import PencilSvg from '@components/icons/Pencil.svg';
import { useModalContext } from '@providers/ModalProvider';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFetchBillingAccountTransactionsQueryFn } from '@pages/Admin/BillingAccountDetailsPage/api/fetchBillingAccountTransactions';
import { AdminService, PrismaBillingAccountDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { billingKeys, TBillingAccountsListParams } from 'app/queries/billing/billingKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { Fragment, useMemo, useState } from 'react';
import { fetchAllBillingAccountsForExport } from '../../api/fetchAllBillingAccountsForExport';
import { createFetchBillingAccountsQueryFn } from '../../api/fetchBillingAccounts';
import { downloadBillingAccountsCsv } from '../../helpers/downloadBillingAccountsCsv';
import { selectRecentTransactions } from './helpers/selectRecentTransactions';
import { InlineTransactionsRow } from './components/InlineTransactionsRow';
import { CellContent } from './styles/CellContent';
import { OrgCell } from './styles/OrgCell';
import { StyledLink } from './styles/StyledLink';
import { Wrapper } from './styles/Wrapper';

type BillingAccountWithDetails = PrismaBillingAccountDto & {
  organizations?: Array<{ id: string; name: string; managementSource?: 'SELF' | 'EXTERNAL' }>;
  MainBalance?: { currentBalance?: number };
  FreeCreditBalance?: { currentBalance?: number };
  ActiveSubscription?: { id: string; Product?: { name?: string } };
  billingConfiguration?: {
    postProcessingRateType?: string;
    postProcessingRate?: number;
    realTimeRateType?: string;
    realTimeRate?: number;
    partialUnlockRateType?: string;
    partialUnlockRate?: number;
    fullUnlockRateType?: string;
    fullUnlockRate?: number;
    enhancedOnDemandRateType?: string;
    enhancedOnDemandRate?: number;
    attorneyConnectFeeRateType?: string;
    attorneyConnectFeeRate?: number;
    attorneyInviteFeeRateType?: string;
    attorneyInviteFeeRate?: number;
  };
};

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal } = useModalContext();
  const { me } = useAuthContext();
  const isVsBilling = me?.roles?.includes('ROLE_BILLING_ADMIN');
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TBillingAccountsListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const accountsQuery = useQuery({
    queryKey: billingKeys.accountList(listParams),
    queryFn: createFetchBillingAccountsQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const refreshAccounts = () => {
    void queryClient.invalidateQueries({ queryKey: billingKeys.accountLists() });
  };

  const exportMutation = useMutation({
    mutationFn: () => fetchAllBillingAccountsForExport(call, { search, orderBy, orderType }),
    onSuccess: ({ data }) => {
      void downloadBillingAccountsCsv(data);
    },
  });

  const recalculateMutation = useMutation({
    mutationFn: (billingAccountId: string) => call(() => AdminService.recalculateBalances({ baId: billingAccountId })),
    onSuccess: refreshAccounts,
  });

  const pauseCollectionMutation = useMutation({
    mutationFn: (subscriptionId: string) =>
      call(() => AdminService.pauseSubscriptionCollection({ requestBody: { subscriptionId } })),
    onSuccess: refreshAccounts,
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: (subscriptionId: string) =>
      call(() => AdminService.cancelSubscription({ requestBody: { subscriptionId } })),
    onSuccess: refreshAccounts,
  });

  const cancelAndVoidMutation = useMutation({
    mutationFn: (subscriptionId: string) =>
      call(() => AdminService.cancelSubscriptionAndVoidInvoice({ requestBody: { subscriptionId } })),
    onSuccess: refreshAccounts,
  });

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const openBalanceModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminAddBalanceToBillingAccount, { billingAccountId, onSuccess: refreshAccounts });
  };

  const openTransactionModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AddAdminTransactionModal, { billingAccountId, onSuccess: refreshAccounts });
  };

  const openRateModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminBillingAccountRate, { billingAccountId, onSuccess: refreshAccounts });
  };

  const openRatesHistoryModal = (billingAccountId: string, billingAccountName: string, orgName: string) => {
    openModal(ModalNamesEnum.AdminBillingAccountRatesHistory, { billingAccountId, billingAccountName, orgName });
  };

  const openRatesModal = (billingAccountId: string) => {
    openModal(ModalNamesEnum.AdminBillingAccountRatesEditor, { billingAccountId, onSuccess: refreshAccounts });
  };

  const recalculateBalances = (billingAccountId: string) => {
    recalculateMutation.mutate(billingAccountId);
  };

  const pauseSubscriptionCollection = (subscriptionId: string) => pauseCollectionMutation.mutateAsync(subscriptionId);

  const cancelSubscription = (subscriptionId: string) => cancelSubscriptionMutation.mutateAsync(subscriptionId);

  const cancelSubscriptionAndVoidInvoice = (subscriptionId: string) =>
    cancelAndVoidMutation.mutateAsync(subscriptionId);

  const billingAccounts = accountsQuery.data ?? { data: [], count: 0 };
  const isLoading =
    accountsQuery.isFetching ||
    isSearchSettling ||
    recalculateMutation.isPending ||
    pauseCollectionMutation.isPending ||
    cancelSubscriptionMutation.isPending ||
    cancelAndVoidMutation.isPending;
  const [expandedBillingAccountId, setExpandedBillingAccountId] = useState('');

  const recentTransactionsQuery = useQuery({
    queryKey: billingKeys.transactionsByAccount(expandedBillingAccountId),
    queryFn: createFetchBillingAccountTransactionsQueryFn(call),
    enabled: expandedBillingAccountId !== '',
    select: selectRecentTransactions,
  });

  const handleToggleTransactions = (account: BillingAccountWithDetails) => {
    if (expandedBillingAccountId === account.id) {
      setExpandedBillingAccountId('');
      return;
    }
    setExpandedBillingAccountId(account.id);
  };

  const getRateDisplay = (label: string, rateType: string | undefined, rate: number | string | null | undefined) => {
    let value = '';
    let color = 'text-[var(--color-text)]';

    if (rateType === 'DISALLOWED') {
      value = 'Disallowed';
      color = 'text-red-500';
    } else if (rateType === 'TIER_RATE') {
      value = 'Tier Rate';
      color = 'text-[var(--color-accent)]';
    } else {
      value = formatPrice(Number(rate ?? 0));
    }

    return (
      <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--color-text-muted)]">{label}:</span>
        <span className={`font-medium ${color}`}>{value}</span>
      </div>
    );
  };

  const columnCount = 10;

  const renderInlineTransactionsRow = (data: BillingAccountWithDetails) => (
    <InlineTransactionsRow
      billingAccountId={data.id}
      billingAccountName={data.name}
      colSpan={columnCount}
      isLoading={expandedBillingAccountId === data.id && recentTransactionsQuery.isLoading}
      isOpen={expandedBillingAccountId === data.id}
      transactions={expandedBillingAccountId === data.id ? recentTransactionsQuery.data : undefined}
      onClose={() => setExpandedBillingAccountId('')}
    />
  );

  return (
    <>
      <Header
        title="Billing Accounts"
        buttonLabel={billingAccounts.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={billingAccounts.data?.length ? () => exportCSV() : undefined}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Organization</Th>
                <Th>Plan</Th>
                <Th>Balance</Th>
                <Th>Tier Rate</Th>
                <Th>Billing Rates</Th>
                <Th>Subscription Actions</Th>
                <Th>Transactions</Th>
                <Th>Recalculate Balance</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(billingAccounts.data ?? []).map((data: BillingAccountWithDetails, index) => {
                const config = (data as any).billingConfiguration;
                const activeSubscription = (data as any).ActiveSubscription;
                const canEditRates =
                  isVsBilling && !isExternallyManagedOrganization(data.organizations?.[0]?.managementSource);

                const handlePauseCollection = async () => {
                  if (
                    window.confirm(
                      'Are you sure you want to pause collection on the last invoice for this subscription?',
                    )
                  ) {
                    try {
                      await pauseSubscriptionCollection(activeSubscription!.id);
                    } catch {
                      alert('Failed to pause subscription collection');
                    }
                  }
                };

                const handleCancel = async () => {
                  if (
                    window.confirm(
                      'Are you sure you want to cancel this subscription? It will not renew at the end of the current period.',
                    )
                  ) {
                    try {
                      await cancelSubscription(activeSubscription!.id);
                    } catch {
                      alert('Failed to cancel subscription');
                    }
                  }
                };

                const handleCancelAndVoid = async () => {
                  if (
                    window.confirm(
                      'Are you sure you want to immediately cancel this subscription and void the last invoice? This action cannot be undone.',
                    )
                  ) {
                    try {
                      await cancelSubscriptionAndVoidInvoice(activeSubscription!.id);
                    } catch {
                      alert('Failed to cancel subscription and void invoice');
                    }
                  }
                };

                return (
                  <Fragment key={data.id}>
                    <Tr>
                      <Td>
                        <CellContent>{index + 1}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <StyledLink
                            to={routes.adminBillingAccountDetails.make(data?.id)}
                            className="font-medium text-[var(--color-title)] hover:text-foreground-muted"
                          >
                            {data.name}
                          </StyledLink>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          {((data as any).organizations ?? []).map((org: any) => {
                            if (!org) {
                              return null;
                            }
                            return (
                              <OrgCell key={org?.id}>
                                <StyledLink to={routes.adminOrganizationDetails.make(org?.id)}>{org?.name}</StyledLink>
                              </OrgCell>
                            );
                          })}
                        </CellContent>
                      </Td>
                      <Td>
                        {(data as any).ActiveSubscription?.Product ? (
                          <CellContent>
                            <TagDefaultRounded>
                              {((data as any).ActiveSubscription.Product?.name ?? '').replace('AutoScriptOne', '')}
                            </TagDefaultRounded>
                          </CellContent>
                        ) : (
                          <CellContent></CellContent>
                        )}
                      </Td>
                      <Td>
                        <CellContent>
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-row items-center gap-2">
                              <div>
                                {formatCentsPrice(
                                  ((data as any).MainBalance?.currentBalance ?? 0) +
                                    ((data as any).FreeCreditBalance?.currentBalance ?? 0),
                                )}
                              </div>
                            </div>
                            <div className="flex flex-row text-xs text-[var(--color-text-muted)]">
                              <small className="mr-2">
                                Main: {formatCentsPrice((data as any).MainBalance?.currentBalance ?? 0)}
                              </small>
                              <small>
                                Free Credit: {formatCentsPrice((data as any).FreeCreditBalance?.currentBalance ?? 0)}
                              </small>
                            </div>
                            {isVsBilling ? (
                              <div className="flex flex-col gap-1">
                                <OutlinedButton onClick={() => openTransactionModal(data.id)}>
                                  + Transaction
                                </OutlinedButton>
                                <OutlinedButton onClick={() => openBalanceModal(data.id)}>
                                  Goodwill Credit
                                </OutlinedButton>
                              </div>
                            ) : null}
                          </div>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          {data.fixedRate ? 'Fixed: ' : ''}
                          {formatPrice(data.tierRate ?? 0)}/hr
                          {canEditRates ? (
                            <IconButtonClean
                              type="button"
                              title="Edit rate"
                              className="ml-[10px]"
                              onClick={() => openRateModal(data.id)}
                            >
                              <PencilSvg />
                            </IconButtonClean>
                          ) : null}
                          {isVsBilling ? (
                            <IconButtonClean
                              type="button"
                              title="Rates history"
                              aria-label="Rates history"
                              className="ml-2"
                              onClick={() =>
                                openRatesHistoryModal(data.id, data.name, (data as any).organizations?.[0]?.name ?? '')
                              }
                            >
                              <HistoryIcon />
                            </IconButtonClean>
                          ) : null}
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent className="relative group">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 min-w-[300px]">
                            {getRateDisplay('Post', config?.postProcessingRateType, config?.postProcessingRate)}
                            {getRateDisplay('Real-time', config?.realTimeRateType, config?.realTimeRate)}
                            {getRateDisplay('Partial', config?.partialUnlockRateType, config?.partialUnlockRate)}
                            {getRateDisplay('Full Unlock', config?.fullUnlockRateType, config?.fullUnlockRate)}
                            {getRateDisplay('Enhanced', config?.enhancedOnDemandRateType, config?.enhancedOnDemandRate)}
                            {getRateDisplay(
                              'Attorney Connect',
                              config?.attorneyConnectFeeRateType,
                              config?.attorneyConnectFeeRate,
                            )}
                            {getRateDisplay(
                              'Attorney Invite',
                              config?.attorneyInviteFeeRateType,
                              config?.attorneyInviteFeeRate,
                            )}
                          </div>
                          {canEditRates ? (
                            <IconButtonClean
                              type="button"
                              title="Edit rates"
                              className="edit-billing-rates"
                              onClick={() => openRatesModal(data?.id ?? '')}
                            >
                              <PencilSvg />
                            </IconButtonClean>
                          ) : null}
                        </CellContent>
                      </Td>
                      <Td>
                        {!activeSubscription || !isVsBilling ? (
                          <CellContent />
                        ) : (
                          <CellContent>
                            <div className="flex flex-col gap-1">
                              <OutlinedButton onClick={handlePauseCollection}>Pause Collection</OutlinedButton>
                              <OutlinedButton onClick={handleCancel}>Cancel Subscription</OutlinedButton>
                              <OutlinedButton onClick={handleCancelAndVoid}>Cancel & Void</OutlinedButton>
                            </div>
                          </CellContent>
                        )}
                      </Td>
                      <Td>
                        <CellContent>
                          <OutlinedButton style={{ width: 'auto' }} onClick={() => handleToggleTransactions(data)}>
                            {expandedBillingAccountId === data.id ? 'Hide recent' : 'View recent'}
                          </OutlinedButton>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <OutlinedButton onClick={() => recalculateBalances(data.id)}>Recalculate</OutlinedButton>
                        </CellContent>
                      </Td>
                    </Tr>
                    {renderInlineTransactionsRow(data)}
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={billingAccounts.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
